/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactElement, ReactNode} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {dlsColor} from '../../theme/theme';
import {
  getMapFloatingButtonStates,
  MAP_FLOATING_BUTTON_LABEL,
  MAP_FLOATING_BUTTON_LABEL_EMPHASIS
} from './mapFloatingButtonStyles';

// defines the available map tool pill sizes...
export enum MapToolPillSize {
  Small = 'small',
  Medium = 'medium'
}

export interface MapToolPillProps {
  icon: ReactElement;
  label: string;
  onClick: React.MouseEventHandler;
  size?: MapToolPillSize;
  className?: string;
  // shows the pill selected - e.g. while the panel it opens is open ...
  isSelected?: boolean;
  disabled?: boolean;
  // content after the label, e.g. a split close button ...
  endAdornment?: ReactNode;
  // the pill's DOM id, e.g. for a panel to open beneath it ...
  id?: string;
}

const useStyles = makeStyles(() => {
  const states = getMapFloatingButtonStates();
  return {
    root : {
      ...states.default,
      ...MAP_FLOATING_BUTTON_LABEL,
      boxSizing      : 'border-box',
      display        : 'flex',
      flexDirection  : 'row',
      alignItems     : 'center',
      justifyContent : 'center',
      gap            : 8,
      padding        : '0 8px',
      // transparent until selected, so selecting never resizes the pill ...
      border         : '1px solid transparent',
      whiteSpace     : 'nowrap',
      cursor         : 'pointer',
      // a hover fills the pill and leaves its label be, as the Time Mode
      // toggle's does ...
      '&:hover'      : states.hover,
      '&:active'     : {
        ...states.active,
        fontWeight : MAP_FLOATING_BUTTON_LABEL_EMPHASIS
      }
    },
    // the label keeps its semi-bold width throughout, so pressing or selecting
    // the pill never resizes it - or moves the pills beside it ...
    label : {
      display       : 'inline-flex',
      flexDirection : 'column',
      '&::after'    : {
        content       : 'attr(data-label)',
        fontWeight    : MAP_FLOATING_BUTTON_LABEL_EMPHASIS,
        height        : 0,
        overflow      : 'hidden',
        visibility    : 'hidden',
        pointerEvents : 'none',
        userSelect    : 'none'
      }
    },
    selected : {
      '&, &:hover, &:active' : {
        ...states.selected,
        fontWeight : MAP_FLOATING_BUTTON_LABEL_EMPHASIS
      }
    },
    disabled : {
      '&, &:hover, &:active' : {
        ...states.disabled,
        cursor     : 'default',
        fontWeight : MAP_FLOATING_BUTTON_LABEL.fontWeight
      }
    },
    small : {
      height       : 28,
      borderRadius : 8
    },
    medium : {
      height       : 32,
      borderRadius : 12
    }
  };
});

// the icon follows the pill's state - grey, dark teal while selected, light
// grey while disabled ...
const getIconColor = (isSelected: boolean, disabled: boolean): string => (
  disabled ? dlsColor('grey4') : isSelected ? dlsColor('darkTeal') : dlsColor('grey1')
);

/**
 * Renders a current theme map tool trigger as a floating pill with a leading
 * icon and a text label, e.g. the widget cluster's Map Styles and Map Layers
 * buttons - in the DLS floating button states: default, hover, active
 * (pressed), selected and disabled. The pill sizes itself to its label.
 *
 * ```typescript
 *
 *   import {MapToolPill, MapToolPillSize} from
 *     '@ion/dls/src/maps/toolbar/MapToolPill';
 *   ...
 *
 *   <MapToolPill icon={<MapLayerIcon size={IconSize.medium} />}
 *     label={translate('maps:controls.layers')}
 *     size={MapToolPillSize.Medium}
 *     isSelected={isLayerChooserOpen}
 *     onClick={toggleLayerChooser} />
 *
 * ```
 *
 * @param   icon         The icon rendered before the label - its colour
 *                       follows the pill's state.
 * @param   label        The pill's text label.
 * @param   onClick      Invoked when the pill is clicked.
 * @param   size         The pill's size, `small` by default.
 * @param   className    An optional class for the pill.
 * @param   isSelected   Shows the pill selected.
 * @param   disabled     Disables the pill.
 * @param   endAdornment Content after the label, e.g. a split close button.
 * @param   id           The pill's DOM id.
 * @returns              The map tool pill.
 */
export const MapToolPill = ({
  icon,
  label,
  onClick,
  size = MapToolPillSize.Small,
  className,
  isSelected = false,
  disabled = false,
  endAdornment,
  id
}: MapToolPillProps) => {
  const classes      = useStyles();
  const showSelected = isSelected && !disabled;

  const pillClassName = classnames(classes.root, classes[size], {
    [classes.selected] : showSelected,
    [classes.disabled] : disabled
  }, className);

  return (
    <div id={id}
      className={pillClassName}
      role='button'
      aria-pressed={showSelected}
      aria-disabled={disabled}
      onClick={disabled ? undefined : onClick}>
      {React.cloneElement(icon, {color: getIconColor(showSelected, disabled)})}
      <span className={classes.label} data-label={label}>
        {label}
      </span>
      {endAdornment}
    </div>
  );
};

export default MapToolPill;
